import { createTheme, alpha } from "@mui/material/styles";
import { ptBR } from "@mui/material/locale";

// UpOwl brand — carried from uw-dashboard-react theme.config.js
// Primary: azul-petróleo. Secondary: amarelo. Fonts: Oswald (display) + Inter (body).
export const brand = {
  primary: {
    main: "#2c4055",
    dark: "#021a2c",
    light: "#576b82",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#fcab10",
    dark: "#c47c00",
    light: "#ffdd50",
    contrastText: "#021a2c",
  },
  success: { main: "#238f62", light: "#5bd39d" },
  warning: { main: "#b7791f", light: "#f6c453" },
  error: { main: "#cf5d5d", light: "#ff7a7a" },
  info: { main: "#4a75c9", light: "#7ba0e6" },
  // NAO existe "gradiente da marca". O logo da UpOwl e monocromatico:
  // uma cor chapada por variante, sem gradiente em nenhum arquivo.
  // Havia aqui `gradient: linear-gradient(135deg, #407899, #791e94)`
  // anunciado como "brand accent used on logos" — as duas cores sao as
  // de `logo-blue.svg` e `logo-purple.svg`, duas variantes ALTERNATIVAS
  // do mesmo desenho. O token nunca foi importado por ninguem.
  // Ver docs/padrao/design-system.md.
  //
  // O de baixo fica: e derivado de cor real do tema (primary.dark ->
  // primary.main) e da o fundo escuro da plataforma. E decisao de
  // interface, nao identidade de marca.
  gradientDark: "linear-gradient(135deg, #021a2c 0%, #2c4055 100%)",
};

export const theme = createTheme(
  {
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      h1: { fontFamily: "'Oswald', sans-serif", fontWeight: 600, letterSpacing: "-0.01em" },
      h2: { fontFamily: "'Oswald', sans-serif", fontWeight: 600, letterSpacing: "-0.01em" },
      h3: { fontFamily: "'Oswald', sans-serif", fontWeight: 600 },
      h4: { fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "1.4rem" },
      h5: { fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "1.15rem" },
      h6: { fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "1rem", letterSpacing: "0.02em" },
      overline: {
        fontFamily: "'Oswald', sans-serif",
        fontSize: "0.7rem",
        letterSpacing: "0.12em",
        fontWeight: 500,
      },
      button: {
        textTransform: "none",
        fontWeight: 500,
        letterSpacing: "0.01em",
      },
      subtitle2: { fontSize: "0.85rem", fontWeight: 500, color: "#576b82" },
    },
    palette: {
      mode: "light",
      primary: brand.primary,
      secondary: brand.secondary,
      success: brand.success,
      warning: brand.warning,
      error: brand.error,
      info: brand.info,
      background: {
        default: "#f4f6fa",
        paper: "#ffffff",
      },
      text: {
        primary: "#172434",
        secondary: "#576b82",
      },
      divider: "#e2e8f0",
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          html, body { -webkit-font-smoothing: antialiased; }
          body { background: #f4f6fa; }
          *::-webkit-scrollbar { width: 10px; height: 10px; }
          *::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 999px; }
          *::-webkit-scrollbar-thumb:hover { background: #a0aec0; }
        `,
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backdropFilter: "blur(8px)",
            backgroundColor: alpha("#ffffff", 0.85),
            color: "#021a2c",
            boxShadow: "0 1px 0 rgba(2,26,44,0.06)",
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          outlined: {
            borderColor: "#e2e8f0",
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0, variant: "outlined" },
        styleOverrides: {
          root: {
            borderRadius: 14,
            transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          title: { fontSize: "0.95rem", fontWeight: 600, color: "#021a2c" },
          subheader: { fontSize: "0.8rem", color: "#576b82" },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            paddingInline: 16,
            paddingBlock: 8,
          },
          contained: {
            boxShadow: "none",
            "&:hover": { boxShadow: "0 4px 12px rgba(2,26,44,0.15)" },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, borderRadius: 8 },
          outlined: { borderColor: "#e2e8f0" },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            marginBottom: 2,
            "&.Mui-selected": {
              backgroundColor: alpha("#fcab10", 0.15),
              color: "#021a2c",
              "&:hover": { backgroundColor: alpha("#fcab10", 0.22) },
              "& .MuiListItemIcon-root": { color: "#c47c00" },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { minWidth: 36, color: "#576b82" },
        },
      },
      MuiTextField: {
        defaultProps: { size: "small", variant: "outlined" },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#576b82",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            borderBottomColor: "#e2e8f0",
          },
          body: {
            fontSize: "0.85rem",
            borderBottomColor: "#e2e8f0",
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: "#e2e8f0" } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: "#021a2c",
            fontSize: "0.75rem",
            borderRadius: 6,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: "0 8px 24px rgba(2,26,44,0.25)",
          },
        },
      },
    },
  },
  ptBR,
);
